/**
 * useQuery hooks for the stories hierarchy (sections + stories) shown in
 * the SetupPDF sidebar panel.
 *
 * Fetches Hasura → transforms to `IAccordionListNode[]`. Consumers read
 * `storiesSelectData` and `isLoadingStories` from `hierarchyQuery.data` and
 * `hierarchyQuery.isLoading`.
 *
 * Pass `null` to disable the query (e.g. before `selectedStoryTypes` is
 * known — the user must pick story types from the modal first).
 *
 * The `selectedStoryTypes` array is part of the query key so changing the
 * filter automatically triggers a fresh fetch without any manual effect.
 *
 * Two `useGraphQLQuery` hooks are used — one for publications, one for
 * sections — each with `enabled` gated on `params.exportType`. This
 * avoids having to conditionally swap the `document` at runtime (which
 * React Hook rules prohibit) while still sharing one exported hook.
 */

import { useGraphQLQuery } from '@opengov/bnp-core';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { IPublicationFolderWithStories, IStoryFolder } from '@opengov/stories-shared/types';
import {
  filterEmptySections,
  generateDataStructureForSections,
  sortListByTitleAndOrder
} from '@opengov/stories-shared/utils/publication';
import { buildPublicationFolderHierarchyMap } from '@opengov/stories-shared/utils/general';
import { getFragmentData, graphql } from '../../../../../gql-generated/hasura';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { publicationSetupPdfQueryKeys, PublicationSetupPdfStylesKeyParams } from './publicationSetupPdfQueryKeys';
import { StoryState } from '../SelectStoryType/SelectStoryTypeModal';
import {
  GetPublicationHierarchyForSetupPdfQuery,
  GetPublicationHierarchyForSetupPdfQueryVariables,
  GetSectionHierarchyForSetupPdfQuery,
  GetSectionHierarchyForSetupPdfQueryVariables
} from '../../../../../gql-generated/hasura/graphql';

export type UseStoriesHierarchyParams = (PublicationSetupPdfStylesKeyParams & {
  selectedStoryTypes: StoryState[];
  entityId: number;
}) | null;

export type IStoriesHierarchyResult = {
  nodes: IAccordionListNode[];
  publicationName?: string;
  publicationCssCode?: string | null;
};

// ---------------------------------------------------------------------------
// GraphQL documents — picked up by codegen since storyFolders and
// publications_by_pk are in @opengov/bnp-stories-hasura-service-schema.
// Fragment name must be globally unique across all files.
// ---------------------------------------------------------------------------

const SetupPdf_StoryFolderFieldsFragment = graphql(`
  fragment SetupPdf_StoryFolderFields on storyFolders {
    id
    title
    order
    parentFolderId
    stories(where: { deletedAt: { _is_null: true }, state: { _in: $state } }, order_by: { order: asc }) {
      id: _id
      title
      order
    }
  }
`);

const GetPublicationHierarchyForSetupPdfDocument = graphql(`
  query GetPublicationHierarchyForSetupPdf($publicationId: uuid!, $entityId: Int!, $state: [storyStates_enum!]) {
    publicationRootFolder: storyFolders(
      where: {
        publicationId: { _eq: $publicationId }
        entityId: { _eq: $entityId }
        parentFolder: { parentFolderId: { _is_null: true } }
      }
    ) {
      ...SetupPdf_StoryFolderFields
    }
    publication: publications_by_pk(id: $publicationId) {
      title
      customCssCode
      publicationFolders: storyFolders(
        where: { publicationId: { _eq: $publicationId }, entityId: { _eq: $entityId }, deletedAt: { _is_null: true } }
      ) {
        ...SetupPdf_StoryFolderFields
        subFolders(where: { deletedAt: { _is_null: true } }) {
          ...SetupPdf_StoryFolderFields
        }
      }
    }
  }
`);

const GetSectionHierarchyForSetupPdfDocument = graphql(`
  query GetSectionHierarchyForSetupPdf($sectionId: uuid!, $entityId: Int!, $state: [storyStates_enum!]) {
    storyFolders(where: { id: { _eq: $sectionId }, entityId: { _eq: $entityId } }) {
      ...SetupPdf_StoryFolderFields
      subFolders(where: { deletedAt: { _is_null: true } }) {
        ...SetupPdf_StoryFolderFields
        subFolders(where: { deletedAt: { _is_null: true } }) {
          ...SetupPdf_StoryFolderFields
          subFolders(where: { deletedAt: { _is_null: true } }) {
            ...SetupPdf_StoryFolderFields
            subFolders(where: { deletedAt: { _is_null: true } }) {
              ...SetupPdf_StoryFolderFields
              subFolders(where: { deletedAt: { _is_null: true } }) {
                ...SetupPdf_StoryFolderFields
              }
            }
          }
        }
      }
    }
  }
`);

// ---------------------------------------------------------------------------
// Transformation helpers
// ---------------------------------------------------------------------------

function transformToStoriesSelectData(sections: IStoryFolder[]): IAccordionListNode[] {
  const filteredSections = sortListByTitleAndOrder(
    filterEmptySections(sections)
  ) as IPublicationFolderWithStories[];
  return generateDataStructureForSections(filteredSections);
}

// ---------------------------------------------------------------------------
// Exported hook
// ---------------------------------------------------------------------------

export function useStoriesHierarchy(params: UseStoriesHierarchyParams) {
  const isPublication = params?.exportType === 'publication';
  const isSection = params?.exportType === 'section';

  // `as unknown as ...` bridges the type gap between StoryState (our enum, defined in
  // SelectStoryTypeModal) and StoryStates_Enum (codegen enum from the Hasura schema).
  // The string values are identical at runtime ('published', 'draft', 'internalPublished')
  // but TypeScript sees them as unrelated named types and rejects a direct cast.
  // Fix: reuse StoryStates_Enum directly in SelectStoryTypeModal and remove these casts.
  const publicationVariables: GetPublicationHierarchyForSetupPdfQueryVariables = params && isPublication
    ? {
      publicationId: params.exportId,
      entityId: params.entityId,
      state: params.selectedStoryTypes as unknown as GetPublicationHierarchyForSetupPdfQueryVariables['state']
    }
    : { publicationId: '', entityId: 0, state: [] };
  const sectionVariables: GetSectionHierarchyForSetupPdfQueryVariables = params && isSection
    ? {
      sectionId: params.exportId,
      entityId: params.entityId,
      state: params.selectedStoryTypes as unknown as GetSectionHierarchyForSetupPdfQueryVariables['state']
    }
    : { sectionId: '', entityId: 0, state: [] };
  const publicationQuery = useGraphQLQuery<
    GetPublicationHierarchyForSetupPdfQuery,
    GetPublicationHierarchyForSetupPdfQueryVariables,
    IStoriesHierarchyResult
  >({
    queryKey: params && isPublication
      ? publicationSetupPdfQueryKeys.hierarchy({
        exportType: params.exportType,
        exportId: params.exportId,
        selectedStoryTypes: params.selectedStoryTypes
      })
      : ([...publicationSetupPdfQueryKeys.all, 'hierarchy', 'publication', 'disabled'] as const),
    document: GetPublicationHierarchyForSetupPdfDocument,
    url: HASURA_SERVICE_URL,
    variables: publicationVariables,
    options: {
      enabled: params !== null && isPublication,
      select: (data: GetPublicationHierarchyForSetupPdfQuery): IStoriesHierarchyResult => {
        const publication = data.publication;
        if (!publication) throw new Error('Publication not found');
        // `as unknown as IStoryFolder[]`: the codegen type hides fields behind fragment
        // masking markers (`' $fragmentRefs'`) until getFragmentData() unmasks them.
        // IStoryFolder expects concrete fields, so the types are structurally incompatible
        // even though the runtime data is the same shape after Hasura fills the fragment.
        const folderHierarchy = buildPublicationFolderHierarchyMap(
          publication.publicationFolders as unknown as IStoryFolder[]
        );
        const rootFolderEntry = data.publicationRootFolder[0];
        if (!rootFolderEntry) throw new Error('Publication root folder not found');
        // Use getFragmentData to unmask the fragment-masked type so we can
        // access the `id` field that the fragment exposes.
        const rootFolderData = getFragmentData(SetupPdf_StoryFolderFieldsFragment, rootFolderEntry);
        const rootFolder = folderHierarchy[rootFolderData.id];
        return {
          // Same fragment-masking gap as above — subFolders on the codegen type
          // carry `' $fragmentRefs'` instead of concrete fields.
          nodes: transformToStoriesSelectData(rootFolder.subFolders as unknown as IStoryFolder[]),
          publicationName: publication.title,
          publicationCssCode: publication.customCssCode ?? null
        };
      }
    }
  });

  const sectionQuery = useGraphQLQuery<
    GetSectionHierarchyForSetupPdfQuery,
    GetSectionHierarchyForSetupPdfQueryVariables,
    IStoriesHierarchyResult
  >({
    queryKey: params && isSection
      ? publicationSetupPdfQueryKeys.hierarchy({
        exportType: params.exportType,
        exportId: params.exportId,
        selectedStoryTypes: params.selectedStoryTypes
      })
      : ([...publicationSetupPdfQueryKeys.all, 'hierarchy', 'section', 'disabled'] as const),
    document: GetSectionHierarchyForSetupPdfDocument,
    url: HASURA_SERVICE_URL,
    variables: sectionVariables,
    options: {
      enabled: params !== null && isSection,
      select: (data: GetSectionHierarchyForSetupPdfQuery): IStoriesHierarchyResult => {
        return {
          // Same fragment-masking gap — storyFolders from the codegen query type
          // uses `' $fragmentRefs'` markers instead of concrete IStoryFolder fields.
          nodes: transformToStoriesSelectData(data.storyFolders as unknown as IStoryFolder[])
        };
      }
    }
  });

  // Return the active query result — only one is enabled at a time.
  return isPublication ? publicationQuery : sectionQuery;
}
